import { apiListComputeClusters } from 'api/computeClusters/listComputeClusters';
import { apiListDeployments } from 'api/deployments/listDeployments';
import { apiListNodepools } from 'api/nodepools/listNodepools';
import { useNodepoolTableData } from 'modules/Compute/hooks/useComputeTableColumns';
import { DeployModelButton } from 'modules/Compute/Components/ComputeActions/DeployModelButton';
import { ActionList } from 'components/ActionList/ActionList';
import { useQuery } from 'react-query';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';

type Params = {
  userOrOrgId: string;
};

export type MyComputeDataType = {
  nodePoolId: string;
  computeClusterId: string;
  cloudProvider: string;
  cloudRegion: string;
  instance: (CF.API.Compute.InstanceType & { price: number })[];
  hardware: CF.API.Compute.InstanceType[];
  cost: number;
  clusterType: string;
  deployments: [];
};

type NodePoolsWithDeployment = {
  status?: CF.API.Status;
  deployments: CF.API.Compute.Deployment[];
  id: string;
  description?: string;
  created_at?: string;
  modified_at?: string;
  user_id?: string;
  compute_cluster: CF.API.Compute.ComputeCluster;
  node_capacity_type: CF.API.Compute.NodeCapacityType;
  instance_types: CF.API.Compute.InstanceType[];
  cost: string;
  type: boolean;
  metadata?: CF.API.Compute.Metadata;
  onDeploy?: () => void;
};

const convertToTableData = (nodePools: NodePoolsWithDeployment[]) => {
  return nodePools?.map((nodePool) => {
    // Actions...
    const nodepoolActions = [<DeployModelButton clusterId={nodePool?.compute_cluster?.id} nodePoolId={nodePool?.id} onClick={nodePool?.onDeploy} />];

    return {
      ...useNodepoolTableData(nodePool as unknown as CF.API.Compute.NodePool),
      deployments: nodePool.deployments,
      actions: <ActionList name={nodePool.id} isButton={false} contextMenuActions={nodepoolActions} />,
    };
  });
};

export const useMyComputeRecord = ({ userOrOrgId }: Params) => {
  const { track } = useDataCollection();
  const { isFetching: isLoading, data: computeRecord = [] } = useQuery(
    ['MyCompute', { userOrOrgId }],
    async () => {
      const dataListComputeClusters = await apiListComputeClusters({ userId: userOrOrgId });

      const ids = dataListComputeClusters.compute_clusters.map(({ id }) => id);
      const promises = ids.map((id) => apiListNodepools({ userId: userOrOrgId, clusterId: id }));
      const dataListNodepools = await Promise.all(promises);
      const nodePools = dataListNodepools.map(({ nodepools }) => nodepools).flat();

      const nodePoolIds = nodePools.map(({ id }) => id);
      const promises2 = nodePoolIds.map((id) => apiListDeployments({ userId: userOrOrgId, nodePoolId: id }));
      const dataListDeployments = await Promise.all(promises2);

      const nodePoolsWithDeployment = nodePools.map((nodePool, index) => {
        return {
          ...nodePool,
          ...dataListDeployments[index],
          onDeploy: () => track('Home Page Advanced Deploy Clicked', { clusterId: nodePool?.compute_cluster?.id, nodepoolId: nodePool?.id }),
        };
      });

      return convertToTableData(nodePoolsWithDeployment as unknown as NodePoolsWithDeployment[]);
    },
    {
      enabled: !!userOrOrgId,
      staleTime: 1000 * 60,
    },
  );

  return { computeRecord, isLoading };
};
